// * Import required libraies
import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { useMemo } from "react";

// * Import 3rd party lib comp
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
} from "@mui/material";
import { Box, makeStyles } from "@material-ui/core";
import { connect } from "react-redux";

// * Import Actions
import { initialActiveWorkspace } from "../../../redux/stateData";
import {
  updateUserScreen,
  updateStandaloneWsForm,
  updateDialogInfo,
} from "../../../redux/actions";
import { masterTrackGaEvent } from "../../../services/ga";

// * Import Utils
import {
  wrapperUpdateWorksheetStatus,
  initialWsFormScreenValue,
  wrapperWs,
} from "../../../utils/analyzeUtils";

// Import Api's
import { deleteWorksheet, updateWorksheetStatus } from "../../../services/api";

// Import Config
import { config } from "../../../config/config";

// Import Custom Components
import AnalyzeShareDialog from "../../Analyze/AnalyzeDashboard/AnalyzeShareDialog";
import WorkspaceCreateHelpers from "./WorkspaceCreateHelpers";

// Import Images
import renameIcon from "../../../../assets/images/renameIcon.svg";
import deleteIcon from "../../../../assets/images/deleteIcon.svg";
import shareIcon from "../../../../assets/images/shareIcon.svg";
import NoSavedReport from "../../../../assets/images/NoSavedReport.svg";

// Import Styling
import themeColors from "../../../../styles/theme/themeColors";
import { ThemeContext } from "../../../contexts/ThemeContext";
import SigviewTooltip from "../../../components/Common/SigviewTooltip";

const WorkspaceTable = (props) => {
  const {
    dispatch: ReduxDispatcher = () => {},
    user = {},
    standaloneWs: wsForm,
    allData = {},
    workbooks = {},
    searchQuery = "",
    setReloadWorkbooks,
  } = props;
  const { state: themeState } = useContext(ThemeContext);
  const themeColors = themeState.themes[themeState.activeTheme];
  const history = useHistory();
  //making styles for all variations
  const useStyles = makeStyles(() => ({
    root: {
      backgroundColor: themeColors["workspacePrimaryColor"],
      borderBottom: "none",
    },
    tableHeader: {
      padding: "6px 16px",
      borderBottom: "none",
      fontWeight: "bold",
    },
  }));
  const classes = useStyles();
  const handleSnackbarMessageShow = (message) => {
    let snackbarPayload = {
      ...user.screen.snackbar,
      open: true,
      message: message,
    };
    const action = updateUserScreen("snackbar", snackbarPayload);
    ReduxDispatcher(action);
  };
  const handleWsFormChange = (key, value) => {
    const payload = { key, value };
    const action = updateStandaloneWsForm(payload);
    ReduxDispatcher(action);
  };
  const handleShowWorkbooksDrawer = (value) => {
    const newScreen = { ...initialWsFormScreenValue, allWorkbooks: value };
    handleWsFormChange("screen", newScreen);
  };
  const handleWorkspaceOpen = (workbook, worksheet) => {
    // Google Analytics Event - Master

    masterTrackGaEvent({
      category: "Workspace",
      action: "OpenWorkspace",
      label: "Open",
    });

    // * ASYNC
    // 0. Redirect to /analyze/dashboard so that the shared URL is removed
    // 1. Close the drawer (Since AnalyzeDashboard will umount, it will anyways close; still putting it if in case logic changes later)
    // 2. Revert setIsAnyworkbookInRedux to unmound AnalyzeDashboard
    // 3. Set new active workspace in redux
    // 4. Reload Worksheet to trigger useEffect
    // 5. Trigger updateWorksheetStatus to update active worksheet in mongo

    // 0. Redirect to /analyze/dashboard so that the shared URL is removed
    history.push("/analyze/dashboard");

    // 1. Close the drawer (Since AnalyzeDashboard will umount, it will anyways close; still putting it if in case logic changes later)

    // Close the drawer
    handleShowWorkbooksDrawer(false);

    // 2. Revert setIsAnyworkbookInRedux to unmound AnalyzeDashboard

    // setIsAnyworkbookInRedux(false);

    // 3. Set new active workspace in redux

    // Set new active workspace in redux
    const activeWorkspacePayload = {
      workbook,
      worksheet,
    };
    const actionValue = {
      activeWorkspace: {
        wsCategory: "saved",
        crudType: "update",
        payload: activeWorkspacePayload,
      },
    };
    var action = updateUserScreen(null, actionValue);
    ReduxDispatcher(action);

    // 4. Reload Worksheet to trigger useEffect

    // setReloadWorksheet(Date.now());

    // 5. Trigger updateWorksheetStatus to update active worksheet in mongo
    const modWorksheet = { ...worksheet, isActive: true };
    const payload = wrapperUpdateWorksheetStatus({
      workbook,
      worksheet: modWorksheet,
    });
    const fetchProps = { payload };
    const updateWorksheetStatusPromise = updateWorksheetStatus(fetchProps);
    updateWorksheetStatusPromise
      .then((responseData) => {
        // Post success, reload the workbook data so that it has the correct active worksheet
        setReloadWorkbooks(Date.now());
      })
      .catch((json) => {
        if (json.error !== config.hardCoded.queryCancelled) {
          console.groupCollapsed("UI ERROR");
          console.log("ERROR -> ", json.error);
          console.log("updateWorksheetStatus API failed");
          console.groupEnd();

          const message = "Updating worksheet status failed";
          handleSnackbarMessageShow(json.error || message);
        }
      });
  };

  const rows =
    workbooks?.result?.workbookList?.flatMap(
      (workbook) =>
        workbook.worksheetList?.map((sheet) => {
          const createdOnValue = sheet?.worksheetObject?.createdOn;

          const formattedDate =
            createdOnValue && !isNaN(new Date(Number(createdOnValue)).getTime())
              ? new Date(Number(createdOnValue)).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : "-";
          // const formattedTime = createdDate.toLocaleTimeString("en-IN", {
          //   hour: "2-digit",
          //   minute: "2-digit",
          //   hour12: true,
          // });

          return {
            id: sheet.sheetId,
            worksheet: sheet,
            workbook,
            worksheetName: sheet.sheetName || "Untitled Sheet",
            createdBy: workbook.emailId || "Unknown",
            createdOn: `${formattedDate}`,
          };
        }) || [],
    ) || [];
  const handleDeleteDialog = (...args) => {
    const message = {
      title: "Are you sure you want to delete?",
      subtitle: "This action cannot be undone",
    };
    const handleNo = () => {
      //Close the dialog box
      ReduxDispatcher(
        updateDialogInfo({
          ...user.dialogInfo,
          open: false,
        }),
      );
      // trackCrudGA("DeleteNo");
    };
    const handleYes = () => {
      //Close the dialog box
      ReduxDispatcher(
        updateDialogInfo({
          ...user.dialogInfo,
          open: false,
          message: { title: "", subtitle: "" },
        }),
      );
      handleDeleteWorksheet.apply(null, args);
      // trackCrudGA("DeleteYes");
    };
    ReduxDispatcher(
      updateDialogInfo({
        ...user.dialogInfo,
        open: true,
        message,
        handleYes,
        handleNo,
      }),
    );
  };
  const handleDeleteWorksheet = (...args) => {
    const [workbook, worksheet, variant = "mainPage"] = args;
    // Google Analytics Event - Master
    masterTrackGaEvent({
      category: "Workspace",
      action: "DeleteWorkspace",
      label: "Delete",
    });

    ReduxDispatcher(updateUserScreen("isDashboardLoading", true));
    const fetchProps = {
      payload: {
        email: user?.reqMetadata?.email,
        workbookId: workbook._id,
        worksheetId: worksheet.sheetId,
        orgViewReq: {
          organization: user?.reqMetadata?.organization,
          view: user?.reqMetadata?.view,
        },
      },
    };

    const deleteChartPromise = deleteWorksheet(fetchProps);
    deleteChartPromise
      .then((responseData) => {
        if (variant === "mainPage") {
          // ! THIS SHOULD BE DONE IN THE APPSERVER in the deleteWorksheet API call
          // 1. Delete the workspace in mongo
          // 2. Get the list of remaining workspaces
          // 3. Set the first one as active

          // ! HANDLING THIS IN THE UI RIGHT NOW
          // 1. Trigger updateWorksheetStatus to update active worksheet in mongo
          const remainingWorksheetsList = workbook.worksheetList.filter(
            (row) => row.sheetId !== worksheet.sheetId,
          );
          const firstWorksheet = remainingWorksheetsList[0]; // to make the first one active
          const modifiedWorkbook = {
            ...workbook,
            worksheetList: remainingWorksheetsList,
          };
          const payload = wrapperUpdateWorksheetStatus({
            workbook: modifiedWorkbook,
            worksheet: firstWorksheet,
          });
          const fetchProps = { payload };
          const updateWorksheetStatusPromise =
            updateWorksheetStatus(fetchProps);
          updateWorksheetStatusPromise
            .then((responseData) => {
              // 1. Revert setIsAnyworkbookInRedux to unmound AnalyzeDashboard

              //   setIsAnyworkbookInRedux(false);

              // 2. Set new active workspace in redux

              // Set new active workspace in redux
              const activeWorkspacePayload = {
                workbook: modifiedWorkbook,
                worksheet: firstWorksheet,
              };
              const actionValue = {
                activeWorkspace: {
                  wsCategory: "saved",
                  crudType: "update",
                  payload: activeWorkspacePayload,
                },
                isDashboardLoading: false,
              };
              var action = updateUserScreen(null, actionValue);
              ReduxDispatcher(action);

              // 3. Reload Worksheet to trigger useEffect

              //   setReloadWorksheet(Date.now());

              // 3. Reload Worksheet to trigger useEffect

              //   setReloadWorkbooks(Date.now());
            })
            .catch((json) => {
              if (json.error !== config.hardCoded.queryCancelled) {
                console.error("UI ERROR - API FAILED");
                console.groupCollapsed("DETAILS");
                console.log("JSON -> ", json);
                console.log("ERROR -> ", json.error);
                console.log("updateWorksheetStatus API failed");
                console.groupEnd();

                // ! If the API fails, there old active workspace will be deleted and there will be no new one that will be active
                // ! Hence, such logics should be done in the App Server
                // setIsAnyworkbookInRedux to false so that the component unmounts
                // setIsAnyworkbookInRedux(false);

                // Update User Screen to initial workspace category so that getAllWorkbooks is called
                const message = "Updating worksheet status failed";
                let snackbarPayload = {
                  ...user.screen.snackbar,
                  open: true,
                  message: json.error || message,
                };
                var actionPayload = {
                  snackbar: snackbarPayload,
                  activeWorkspace: initialActiveWorkspace, // This will also reload the workbook data so that it has the correct active worksheet (AnalyzeDashboard.js - useEffect)
                  isDashboardLoading: false,
                };
                var action = updateUserScreen(null, actionPayload);
                ReduxDispatcher(action);
              }
            });
        } else if (variant === "sidenav") {
          let snackbarPayload = {
            ...user.screen.snackbar,
            open: true,
            message: "Worksheet deleted successfully!",
          };
          var actionPayload = {
            snackbar: snackbarPayload,
            isDashboardLoading: false,
          };
          // Update User Screen
          var action = updateUserScreen(null, actionPayload);
          ReduxDispatcher(action);

          //   setReloadWorkbooks(Date.now());
        }
      })
      .catch((json) => {
        console.error("UI ERROR - API FAILED");
        console.groupCollapsed("DETAILS");
        console.log("Error JSON -> ", json);
        console.groupEnd();

        var snackbarPayload = {
          ...user.screen.snackbar,
          open: true,
          message: json.error || "Deleting workspace failed",
        };
        var actionPayload = {
          snackbar: snackbarPayload,
          isDashboardLoading: false,
        };
        // Update User Screen
        var action = updateUserScreen(null, actionPayload);
        ReduxDispatcher(action);
      });
  };
  const handleShare = (workbook, worksheet) => {
    // Google Analytics Event - Master
    masterTrackGaEvent({
      category: "Share",
      action: "ShareURLOpenInSidenav",
      label: "OpenShare",
    });

    const workbookId = user.screen.activeWorkspace.payload.workbook?._id;
    const backendWs = wrapperWs({ user, allData, wsForm, workbookId });
    const payload = {
      ...worksheet.worksheetObject,
      orgViewReq: backendWs.orgViewReq,
    };

    const actionPayload = {
      open: true,
      children: (
        <AnalyzeShareDialog
          user={user}
          ReduxDispatcher={ReduxDispatcher}
          payload={payload}
          type="worksheet"
        />
      ),
    };
    const action = updateUserScreen("sigviewDialog", actionPayload);
    ReduxDispatcher(action);
  };

  const handleWorkspaceNameChangeDialog = (
    worksheetName,
    worksheetId,
    workbookId,
    setReloadWorkbooks,
  ) => {
    // trackCrudGA("Clone");
    const initialName = worksheetName;
    const payload = {
      open: true,
      children: (
        <WorkspaceCreateHelpers
          ReduxDispatcher={ReduxDispatcher}
          initialName={initialName}
          user={user}
          worksheetId={worksheetId}
          workbookId={workbookId}
          allData={allData}
          setReloadWorkbooks={setReloadWorkbooks}
        />
      ),
    };
    const action = updateUserScreen("sigviewDialog", payload);
    ReduxDispatcher(action);
  };
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;

    const lower = searchQuery.toLowerCase();

    return rows.filter((row) =>
      row.worksheetName.toLowerCase().includes(lower),
    );
  }, [rows, searchQuery]);
  console.log("bassi", filteredRows);
  return (
    <TableContainer component={Paper} elevation={0}>
      <Table>
        <TableHead>
          <TableRow className={classes.root}>
            <TableCell
              sx={{
                padding: "6px 16px",
                borderBottom: "none",
                fontWeight: "bold",
                fontSize: "0.85rem",
              }}
            >
              Worksheet Name
            </TableCell>
            <TableCell
              sx={{
                padding: "6px 16px",
                borderBottom: "none",
                fontWeight: "bold",
                fontSize: "0.85rem",
              }}
            >
              Created By
            </TableCell>
            <TableCell
              sx={{
                padding: "6px 16px",
                borderBottom: "none",
                fontWeight: "bold",
                fontSize: "0.85rem",
              }}
            >
              Created On
            </TableCell>
            <TableCell
              sx={{
                padding: "6px 16px",
                borderBottom: "none",
                fontWeight: "bold",
                fontSize: "0.85rem",
              }}
            >
              Action
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {filteredRows.length > 0 ? (
            filteredRows.map((row) => (
              <TableRow
                key={row.id}
                hover
                onClick={() => handleWorkspaceOpen(row.workbook, row.worksheet)}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                  borderBottom: "none",
                }}
              >
                <TableCell
                  sx={{
                    maxWidth: 200,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    padding: "4px 16px",
                    borderBottom: "none",
                    fontSize: "0.8rem",
                  }}
                  title={row.worksheetName}
                >
                  {row.worksheetName}
                </TableCell>
                <TableCell
                  sx={{
                    padding: "4px 16px",
                    borderBottom: "none",
                    fontSize: "0.8rem",
                  }}
                >
                  {row.createdBy}
                </TableCell>
                <TableCell
                  sx={{
                    padding: "4px 16px",
                    borderBottom: "none",
                    fontSize: "0.8rem",
                  }}
                >
                  {row.createdOn}
                </TableCell>
                <TableCell
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    padding: "4px 16px",
                    borderBottom: "none",
                    display: "flex",
                    justifyContent: "flex-start",
                    gap: 2,
                    mt: 1,
                    alignItems: "center",
                    fontSize: "0.8rem",
                  }}
                >
                  <SigviewTooltip title="Rename">
                    <Box
                      className="workspaceRenameClicked-GA"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWorkspaceNameChangeDialog(
                          row.worksheet.sheetName,
                          row.worksheet.sheetId,
                          row.workbook._id,
                          setReloadWorkbooks,
                        );
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <img src={renameIcon} alt="Rename" />
                    </Box>
                  </SigviewTooltip>
                  <SigviewTooltip title="Delete">
                    <Box
                      className="workspaceDeletdClicked-GA"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDialog(
                          row.workbook,
                          row.worksheet,
                          "mainPage",
                        );
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <img src={deleteIcon} alt="Delete" />
                    </Box>
                  </SigviewTooltip>
                  <SigviewTooltip title="Share">
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShare(row.workbook, row.worksheet);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <img src={shareIcon} alt="Share" />
                    </Box>
                  </SigviewTooltip>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={4}
                align="center"
                sx={{ borderBottom: "none" }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  <img
                    src={NoSavedReport}
                    alt="No records found"
                    style={{
                      width: "300px",
                      height: "auto",
                      marginBottom: "12px",
                      marginTop: "120px",
                    }}
                  />
                  <p style={{ color: "#6c757d", fontSize: "16px", margin: 0 }}>
                    No Worksheet found
                  </p>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
const mapStateToProps = (state) => ({
  user: state.user,
  allData: state.data,
  globalFilters: state.globalFilters,
  standaloneWs: state.standaloneWs,
});

export default connect(mapStateToProps)(WorkspaceTable);